import { asLink, asText, Content, isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import styles from 'components/PRISMIC/scss/pages.module.scss';
import cx from 'classnames';
import PrismicInlineText from 'components/PRISMIC/PrismicInlineText';
import { Icon } from 'components/@tedui';
import { CarouselSliceDefaultItem } from 'components/../../prismicio-types';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import PrismicStyledText from 'components/PRISMIC/PrismicStyledText';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, {
  Autoplay,
  Mousewheel,
  Navigation,
  Pagination
} from 'swiper';
import { useState, type JSX } from 'react';

/**
 * Props for `Carousel`.
 */
export type CarouselProps = SliceComponentProps<Content.CarouselSlice>;

/**
 * Component for "Carousel" Slices.
 */

SwiperCore.use([Mousewheel, Navigation, Pagination, Autoplay]);

function SlideImage(url: string) {
  return (
    <div
      className={styles['pages-carousel__image']}
      style={{
        backgroundImage: `url(${url})`
      }}
    />
  );
}

const Carousel = ({
  slice: {
    primary: { heading, auto_advance, pause_on_hover, interval },
    variation,
    items,
    slice_type
  }
}: CarouselProps): JSX.Element => {
  const carouselId = `carousel-${Math.floor(Math.random() * 0xffffffffff)
    .toString(16)
    .padEnd(6, '0')}`;

  const [prevButtonRef, setPrevButtonRef] = useState<HTMLSpanElement | null>(
    null
  );
  const [nextButtonRef, setNextButtonRef] = useState<HTMLSpanElement | null>(
    null
  );

  return (
    <div
      className={cx(styles['pages-module'], '[&_a]:no-underline')}
      data-slice-type={slice_type}
      data-slice-variation={variation}
    >
      <h3 className={cx(styles.h3, styles.m3)}>
        <PrismicInlineText field={heading} />
      </h3>
      <div className={styles['pages-carousel']} id={carouselId}>
        <div className={cx(styles.swipe)}>
          <Swiper
            loop
            navigation={{
              nextEl: nextButtonRef,
              prevEl: prevButtonRef
            }}
            pagination={{
              bulletClass: 'cursor-pointer swiper-pagination-bullet',
              bulletActiveClass: 'swiper-active-bullet-prismic',
              clickable: true,
              clickableClass: '!bottom-0'
            }}
            autoplay={{
              delay: auto_advance ? interval || undefined : undefined,
              pauseOnMouseEnter: pause_on_hover
            }}
            className={styles.swipe__pages}
          >
            {items.map((item: CarouselSliceDefaultItem) => (
              <SwiperSlide
                key={asText(item.heading)}
                className={styles.swipe__page}
              >
                <div className={styles['pages-carousel__spacer']}>
                  <div className={styles['pages-carousel__spacer__inner']} />
                </div>

                {isFilled.image(item.image)
                  ? SlideImage(item.image.url)
                  : item.legacy_image_url && SlideImage(item.legacy_image_url)}
                <div
                  className={cx(
                    styles['pages-carousel__content'],
                    styles.inverse,
                    styles.module
                  )}
                >
                  {isFilled.richText(item.kicker) && (
                    <div
                      className={cx(
                        styles['pages-carousel__kicker'],
                        styles.e1
                      )}
                    >
                      <PrismicInlineText field={item.kicker} />
                    </div>
                  )}
                  {isFilled.richText(item.heading) && (
                    <PrismicConditionalLink field={item.link}>
                      <div className={cx(styles['pages-carousel__heading'])}>
                        <PrismicInlineText field={item.heading} />
                      </div>
                    </PrismicConditionalLink>
                  )}
                  {isFilled.richText(item.body) && (
                    <div className={styles['pages-carousel__text']}>
                      <div className={styles['pages-carousel__body']}>
                        <PrismicStyledText field={item.body} />
                      </div>
                    </div>
                  )}
                  {isFilled.richText(item.credit) && (
                    <div className={styles['pages-carousel__credit']}>
                      <PrismicInlineText field={item.credit} />
                    </div>
                  )}
                  {isFilled.keyText(item.link_text) && (
                    <a
                      href={asLink(item.link) || ''}
                      className={cx(
                        styles['pages-carousel__link'],
                        styles.button,
                        styles['button--dark']
                      )}
                    >
                      {item.link_text}
                    </a>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <span
          ref={setPrevButtonRef}
          className={cx(
            styles['pages-carousel__flipper'],
            styles['pages-carousel__flipper--prev'],
            'z-10 cursor-pointer'
          )}
        >
          <Icon
            iconName="arrow-left-circle"
            className={cx(styles['pages-carousel__flipper__icon'])}
          />
        </span>
        <span
          ref={setNextButtonRef}
          className={cx(
            styles['pages-carousel__flipper'],
            styles['pages-carousel__flipper--next'],
            'z-10 cursor-pointer'
          )}
        >
          <Icon
            iconName="arrow-right-circle"
            className={cx(styles['pages-carousel__flipper__icon'])}
          />
        </span>
      </div>
    </div>
  );
};

export default Carousel;
